import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "../redux-hooks";
import { addMultiplePaymentRecord, getPaymentNoteById, getPaymentsByInvoiceId, paymentById, paymentSearchService, saveAgencyCommissionPayoutData, saveInvoicePaymentData, saveMultipleCommissionsService, uploadCommissionPaymentFileData, uploadPaymentFileData, uploadPaymentNotesData, voidPaymentService } from "../../../service/PaymentService";
import { GeneralV3Response, InterceptorOption, LoadingType } from "../../../types/CommonTypes";
import { fetchPracticesByUserRoleIvrOrderSkipLoading } from "../../../service/PracticeService";
import { PracticeInfo } from "../practiceSlice";

export type PaymentNotes = {
  attachmentId: number;
  noteType: string;
  content?: string;
  attachment: string;
  documentName: string;
  fileType: string;
  xrefTable: string;
  xrefId: string;
  auditUserName?: string,
  auditTimestamp? : string,
  comments?: string, 
};

export type PaymentNoteDetail = {
  noteId: number;
  xrefTable: string;
  xrefId: number;
  noteType: string;
  content?: string;
  attachementId: number;
  documentName: string;
  fileType: string;  
  auditUserName?: string,
  auditTimestamp? : string,
  comments?: string, 
};

type PaymentState = {
  paymentRequest: PaymentRequest | null;
  paymentRequestStatus?: LoadingType;
  commissionPaymentRequest: commissionPaymentRequest | null;
  commissionPaymentRequestStatus?: LoadingType;
  recordPayoutInfo: RecordPayoutInfo | null;
  searchPaymentList: PaginationSearchPaymentList | null;
  paymentById: PaymentInfo | null;
  paymentByIdStatus: LoadingType;
  recordPayoutInfoStatus?: LoadingType;
  searchPaymentStatus: LoadingType;
  paymentNotesList?: PaymentNoteDetail[]; //CS-1212
  paymentNotesUploadStatus?: LoadingType; //CS-1212
  paymentNotesFetchStatus?: LoadingType; //CS-1212
  voidPaymentStatus: LoadingType;
  paymentsByInvoiceIdStatus: LoadingType;
  paymentsByInvoiceId?: PaymentsByInvoiceResponse;
  practiceListByUserRoleStatus: LoadingType;
  practiceByUserRolePaymentSlice?: PracticeInfo[] | null;
};

const initialState: PaymentState = {
  paymentRequest: null,
  paymentRequestStatus: "idle",
  commissionPaymentRequest: null,
  paymentByIdStatus: 'idle',
  commissionPaymentRequestStatus: "idle",
  recordPayoutInfo: null,
  searchPaymentList: null,
  paymentById: null,

  recordPayoutInfoStatus: "idle",
  searchPaymentStatus: 'idle',
  paymentNotesList: [], //CS-1212
  paymentNotesUploadStatus: "idle", //CS-1212
  paymentNotesFetchStatus: "idle", //CS-1212
  voidPaymentStatus: 'idle',
  paymentsByInvoiceIdStatus: 'idle',
  paymentsByInvoiceId: undefined,
  practiceListByUserRoleStatus: 'idle',
  practiceByUserRolePaymentSlice: [],
};

export type PaymentRequest = {
  content: string;
  documentName: string;
  fileType: string;
  comments: string;
  type: string;
};

export type commissionPaymentRequest = {
  content: string;
  documentName: string;
  fileType: string;
  comments: string;
  type: string;
};
export type AddPaymentInfo={
  invoiceId : number;
  paymentDate :string;
  paidAmount:number;
  paymentComments:string|null
}
export type RecordPayoutInfo = {
  invoiceIds: number[];
  commissionAgencyType: "AGENCY" | "OVERRIDE";
  payment: number;
  payoutDate: string;
  payoutComment: string;
  fromDate?: string;
  toDate?: string;
  /** @deprecated Used only by override payout flow when required by backend */
  agencyId?: number;
  /** @deprecated No longer sent for agency payout recording */
  v3CommissionReceivedAmount?: number;
};

export type SearchPaymentRequest = {
  practiceIds: number[];
  manufacturerIds: number[];
  productIds?: number[]; //CS-2103
  invoiceNumber: string;
  paymentStatus: string[],
  currentPage: number;
  itemsPerPage: number;
  paymentType?: string[]; //CS-1576
  paymentMethod?: string[]; //CS-1512
  agencyIds?:number[]
  invoiceFromDate?: string | undefined;
  invoiceToDate?: string | undefined;
  paidFromDate?: string | undefined;
  paidToDate?: string | undefined;
  orderNumber?: string;
  accRepId?: number;
  paymentAmountMin?: string;
  paymentAmountMax?: string;
  invoiceDateRange?: string;
  paidDateRange?: string;
  alertPayments?: boolean; //CS-1576
  transactionId?: string; 
  purchaseAndBillFilter?: string;
  // Optional. Absent values must keep the existing payment search order.
  sortBy?: "invoiceNumber" | "transactionId" | "invoiceAmount" | "paymentAmount" | "invoiceDate" | "manufacturerName" | "practiceName" | "orderNumber" | "paymentDate" | "agencyName" | "accountRepNames" | "paymentType" | "paymentStatus";
  sortOrder?: "asc" | "desc";
};

export interface PaginationSearchPaymentList {
  content: PaymentInfo[];
  totalElements: number;
  totalPages: number;
  size: number;
}

export type PaymentInfo = {
  paymentId: number;
  invoiceNumber: string | JSX.Element,
  transactionId?: string,
  orderNumber: string | JSX.Element,
  practiceName: string,
  manufacturerName: string,
  paymentMethod: string, //CS-1512
  paymentAmount: string,
  paymentDate: string,
  paymentStatus: string,
  invoiceAmount: string,
  platformFee?: string, //CS-1512
  totalPaidAmount?: string, //CS-1512
  paymentType?: string; //CS-1576
  accountRepNames:string[];
  invoiceDate: string,
  agencyName: string,
  isUpfrontOrder?: boolean;
};

export type CommissionInfo = {
  paymentId: number;
  agencyId: number;
  agencyType: string;
  agencyName: string;
  paymentAmount: number;
  paymentDate: string;
  paymentStatus: string;
  expectedCommissionRate: string;
  expectedCommissionAmount: number;
  calculatedCommissionAmount: number;
  invoiceId: number;
  invoiceLevel: string;
  commissionStatus: string;
  expectedDate: string;
  commissionReceivedDate: string;
};

export type PaymentElementInfo = {
  paymentId: number;
  commissions: CommissionInfo[];
  paymentDate: string;
  paymentAmount: number;
  paymentStatus: string;
  /** Commission-earning portion after shipping / late-fee deductions (from backend). */
  adjustedAmount?: number;
  hasShippingDeduction?: boolean;
  shippingChargeDeducted?: number;
  hasLateFeeDeduction?: boolean;
  lateFeeDeducted?: number;
};

export type PaymentsByInvoiceResponse = {
  payments: PaymentElementInfo[];
  invoiceId: number;
  totalPayments: number;
};

// CS - 1216 Start
export type MultiplePaymentsObj = {

  paymentRequests?: AddPaymentInfo[];
  [key: string]: any;
};
// CS - 1216 End

export const uploadPayment = createAsyncThunk<any, {paymentData: PaymentRequest, interceptorOption: InterceptorOption}>(
  "uploadPayment",
  async (data, {rejectWithValue}) => {
    try{
      const {paymentData, interceptorOption} = data;
      const response = await uploadPaymentFileData(paymentData, interceptorOption);
      return response;
    } catch(error) {
      //@ts-ignore
      return rejectWithValue(error?.data);
    }
  }
);

export const uploadCommissionPayment = createAsyncThunk<any, {commissionData: commissionPaymentRequest, interceptorOption: InterceptorOption}>(
  "uploadCommissionPayment",
  async (data, {rejectWithValue}) => {
    try{
      const {commissionData, interceptorOption} = data;
      const response = await uploadCommissionPaymentFileData(commissionData, interceptorOption);
      return response;
    } catch(error) {
      //@ts-ignore
      return rejectWithValue(error?.data)
    }
  }
);

export const savePayoutInfo = createAsyncThunk(
  "savePayoutInfo",
  async (data: RecordPayoutInfo) => {
    const response = await saveAgencyCommissionPayoutData(data);
    return response.data;
  }
);

export const saveMultipleCommissions = createAsyncThunk(
  "savePayoutInfo",
  async (data: RecordPayoutInfo) => {
    const response = await saveMultipleCommissionsService(data);
    return response.data;
  }
);

export const saveRecordPaymentInfo = createAsyncThunk(
  "saveRecordPaymentInfo",
  async (data: AddPaymentInfo) => {
    const response = await saveInvoicePaymentData(data);
    return response.data;
  }
);

export const searchPayments = createAsyncThunk("searchPayments", async (searchPaymentRequest: SearchPaymentRequest) => {
  const response = await paymentSearchService(searchPaymentRequest);
  const v3Response = response.data;
  return v3Response;
});

// CS - 1216 Start
export const recordMultiplePayments = createAsyncThunk("recordMultiplePayments", async (data: MultiplePaymentsObj) => {
  const response = await addMultiplePaymentRecord(data);
  const v3Response = response.data;
  return v3Response;
});
// CS - 1216 End

export const uploadPaymentNotes = createAsyncThunk(
  "uploadPaymentNotes",
  async (data: any) => {
    const response = await uploadPaymentNotesData(data);
    return response.data;
  }
);

export const fetchPaymentNotesById = createAsyncThunk("fetchPaymentNotesById", async (xrefTable: number) => {
  const response = await getPaymentNoteById(xrefTable, 'PAYMENT');
  const v3Response = response.data;
  return v3Response;
});

export const voidPayment = createAsyncThunk("voidPayment", async (data: number) => {
  const response = await voidPaymentService(data);
  const v3Response = response.data;
  return v3Response;
});

export const getPaymentById = createAsyncThunk("getPaymentById", async (data: number) => {
  const response = await paymentById(data);
  const v3Response = response.data;
  return v3Response;
});

export const getPaymentsByInvoice = createAsyncThunk("getPaymentsByInvoice", async (invoiceId: number) => {
  const response = await getPaymentsByInvoiceId(invoiceId);
  
  const v3Response = response.data;
  console.log(v3Response)
  return v3Response;
});

export const getPracticeListByUserRolePaymentSlice = createAsyncThunk<any, { isActivePracticeOnly?: boolean, skipLoader?: boolean }>('getPracticeListByUserRoleCreateIVRSlice', async ({ isActivePracticeOnly, skipLoader = false }) => {
  const response = await fetchPracticesByUserRoleIvrOrderSkipLoading(!!isActivePracticeOnly, { 'x-skip-loader': skipLoader.toString() });
  const v3Response = response;
  return v3Response.data;
});

const paymentSlice = createSlice({
  name: "payment",
  initialState,
    reducers: {
      resetPaymentNotes: (state: PaymentState) => {
        state.paymentNotesList = []
      },
      resetPaymentById: (state: PaymentState) => {
        state.paymentById = null;
      },
      resetPaymentsByInvoiceId: (state: PaymentState) => {
        state.paymentsByInvoiceId = undefined;
      },
      resetPaymentList: (state: PaymentState) => {
        state.searchPaymentList = null
      }
    },
  extraReducers: (builder) => {
    builder
      .addCase(uploadPayment.pending, (state) => {
        state.paymentRequestStatus = "loading";
      })
      .addCase(
        uploadPayment.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.paymentRequestStatus = "success";
        }
      )
      .addCase(uploadPayment.rejected, (state, action) => {
        state.paymentRequestStatus = "error";
      })
      .addCase(uploadCommissionPayment.pending, (state) => {
        state.commissionPaymentRequestStatus = "loading";
      })
      .addCase(
        uploadCommissionPayment.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.commissionPaymentRequestStatus = "success";
        }
      )
      .addCase(uploadCommissionPayment.rejected, (state, action) => {
        state.commissionPaymentRequestStatus = "error";
      })
      .addCase(savePayoutInfo.pending, (state) => {
        state.recordPayoutInfoStatus = "loading";
      })
      .addCase(
        savePayoutInfo.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.recordPayoutInfoStatus = "success";
        }
      )
      .addCase(savePayoutInfo.rejected, (state, action) => {
        state.recordPayoutInfoStatus = "error";
      })
      .addCase(searchPayments.pending, (state) => {
        state.searchPaymentStatus = "loading";
      })
      .addCase(searchPayments.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        // state.searchPaymentList = action.payload.data?.searchPaymentList;
        state.searchPaymentList = action.payload?.data?.searchPaymentList; // CS-1984
        state.searchPaymentStatus = "success";
      })
      .addCase(searchPayments.rejected, (state, action) => {
        state.searchPaymentStatus = "error";
      })
      .addCase(uploadPaymentNotes.pending, (state) => {
        state.paymentNotesUploadStatus = "loading";
      })
      .addCase(
        uploadPaymentNotes.fulfilled,
        (state, action: PayloadAction<GeneralV3Response>) => {
          state.paymentNotesUploadStatus = "success";
        }
      )
      .addCase(uploadPaymentNotes.rejected, (state, action) => {
        state.paymentNotesUploadStatus = "error";
      })
      .addCase(fetchPaymentNotesById.pending, (state) => {
        state.paymentNotesFetchStatus = "loading";
      })
      .addCase(fetchPaymentNotesById.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.paymentNotesFetchStatus = "success";
        // state.paymentNotesList = action.payload.data;
        state.paymentNotesList = action.payload?.data; // CS-1984
      })
      .addCase(fetchPaymentNotesById.rejected, (state, action) => {
        state.paymentNotesFetchStatus = "error";
      })
      .addCase(voidPayment.pending, (state) => {
        state.searchPaymentStatus = "loading";
      })
      .addCase(voidPayment.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.voidPaymentStatus = "success";
      })
      .addCase(voidPayment.rejected, (state, action) => {
        state.searchPaymentStatus = "error";
      })
      .addCase(getPaymentById.pending, (state) => {
        state.paymentByIdStatus = "loading";
      })
      .addCase(getPaymentById.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.paymentByIdStatus = "success";
        // state.paymentById = action.payload.data;
        state.paymentById = action.payload?.data; // CS-1984
      })
      .addCase(getPaymentById.rejected, (state, action) => {
        state.paymentByIdStatus = "error";
      })
      .addCase(getPaymentsByInvoice.pending, (state) => {
        state.paymentsByInvoiceIdStatus = "loading";
      })
      .addCase(getPaymentsByInvoice.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.paymentsByInvoiceIdStatus = "success";
        // state.paymentsByInvoiceId = action.payload.data;
        state.paymentsByInvoiceId = action.payload?.data; // CS-1984
      })
      .addCase(getPaymentsByInvoice.rejected, (state, action) => {
        state.paymentsByInvoiceIdStatus = "error";
      })
      .addCase(getPracticeListByUserRolePaymentSlice.pending, (state) => {
        state.practiceListByUserRoleStatus = "loading";
      })
      .addCase(getPracticeListByUserRolePaymentSlice.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.practiceListByUserRoleStatus = "success";
        // state.practiceByUserRolePaymentSlice = action.payload.data;
        state.practiceByUserRolePaymentSlice = action.payload?.data; // CS-1984
      })
      .addCase(getPracticeListByUserRolePaymentSlice.rejected, (state, action) => {
        state.practiceListByUserRoleStatus = "error";
      })
  },
});

export const { resetPaymentNotes, resetPaymentById, resetPaymentsByInvoiceId, resetPaymentList } = paymentSlice.actions;
export const usePaymentSlice = () =>
  useAppSelector((state) => state.paymentSlice);
export default paymentSlice.reducer;
